import { addDays, format, isBefore, isValid, parse } from 'date-fns';

export const DATETIME_FORMAT_ISO = "yyyy-MM-dd'T'HH:mm:ss";
const DATE_FORMAT_DASH = 'yyyy-MM-dd';

const stringDateToFormat = (stringDate: string, fromFormat: string): Date | undefined => {
  const parsedDate = parse(stringDate, fromFormat, new Date());
  if (!isValid(parsedDate) || format(parsedDate, fromFormat) !== stringDate) {
    return undefined;
  }

  return parsedDate;
};

const addDate = (date: Date, amount: number) => {
  return addDays(date, amount);
};

const isPastDay = (date1: Date, date2: Date): boolean => {
  return isBefore(date1.setHours(0, 0, 0, 0), date2.setHours(0, 0, 0, 0));
};

/**
 * yyyyMMddHHmmss -> yyyy-MM-dd'T'HH:mm:ss に変換する
 *
 * - 入力は14文字の数字列を想定しています（それ以外の場合は空文字を返します）。
 * - 単純な文字列変換のみを行うため、日付妥当性チェックは行わない
 *
 * @param input 例: "20260217123045"
 * @returns 例: "2026-02-17T12:30:45" または入力が無効な場合は空文字
 */
export const getIsoDatetimeFromJobDatetime = (input: string): string => {
  if (typeof input !== 'string' || input.length !== 14 || !/^\d{14}$/.test(input)) {
    return '';
  }

  const y = input.slice(0, 4);
  const M = input.slice(4, 6);
  const d = input.slice(6, 8);
  const H = input.slice(8, 10);
  const m = input.slice(10, 12);
  const s = input.slice(12, 14);

  return `${y}-${M}-${d}T${H}:${m}:${s}`;
};

/**
 * yyyy-MM-dd 形式の日付文字列を yyyy-MM-ddTHH:mm:ss 形式のISO文字列へ変換します。
 *
 * - 入力が空文字、または yyyy-MM-dd として無効な場合は空文字を返します。
 *
 * @param stringDate - 変換対象の日付文字列（yyyy-MM-dd）
 * @returns ISO形式の日付文字列（yyyy-MM-ddTHH:mm:ss）または空文字
 */
export const convertStringDateToIsoDateString = (stringDate: string): string => {
  if (!stringDate) {
    return '';
  }

  const parsedDate = parse(stringDate, DATE_FORMAT_DASH, new Date());
  if (!isValid(parsedDate) || format(parsedDate, DATE_FORMAT_DASH) !== stringDate) {
    return '';
  }

  return format(parsedDate, DATETIME_FORMAT_ISO);
};

/**
 * APIから取得した日付文字列（例: "2023-02-15"）を
 * "yyyy-MM-ddTHH:mm:ss" 形式の文字列に変換します。
 *
 * - 入力は yyyy-MM-dd 形式を想定しています。
 *
 * @param input - APIレスポンスの日付文字列(yyyy-MM-dd形式)
 * @returns ISO形式の日付文字列（時分秒は全て0とします）
 */
export const getISODateFormatFromApiResponseStringDate = (input: string): string => {
  return convertStringDateToIsoDateString(input);
};

/**
 *  対象日付が比較対象日付からn日以上前かどうかを判定します。
 *
 * - targetDate または dateOfComparsion が falsy な値（例: null、空文字など）の場合は false を返します。
 * - targetDate と dateOfComparsion はともに有効な日付文字列であることを前提としています。無効な日付文字列が渡された場合の挙動は保証されません。
 * - targetDate が dateOfComparsion よりn日以上前の場合は true を返し、それ以外の場合は false を返します。
 * @param targetDate
 * @param dateOfComparsion
 * @param n 日数
 * @returns 対象日付が比較対象日付からn日以上前の場合は true、それ以外の場合は false
 */
export const isTargetDateBeforeComparisonDateByNDays = (targetDate: string, dateOfComparsion: string, n: number): boolean => {
  if (n < 1) {
    return false;
  }
  if (!targetDate || !dateOfComparsion) {
    return false;
  }

  const formatedUpdateDate = getISODateFormatFromApiResponseStringDate(targetDate);
  const formatedDateOfComparsion = getIsoDatetimeFromJobDatetime(dateOfComparsion);

  const target = stringDateToFormat(formatedUpdateDate, DATETIME_FORMAT_ISO);
  const base = stringDateToFormat(formatedDateOfComparsion, DATETIME_FORMAT_ISO);

  if (!target || !base) {
    return false;
  }
  return isPastDay(addDate(target, n - 1), base);
};
